import { For, Show, type JSX } from 'solid-js'
import type { ViewProps } from 'wdc-cube-solid'
import type { DashboardScope, SelectorScope } from 'mormaii-web-presentation/dashboard'

import { CategoryBars, DurationBars, SeriesChart } from '../../../components/charts'
import { CompanyPicker } from '../../../components/company-picker'
import { PeriodPicker } from '../../../components/period-picker'
import { ErrorNotice, Loading, Menu, MetricCard, Panel, Spinner } from '../../../components/ui'
import { Icon } from '../../../components/icons'

import Css from './dashboard.module.scss'

/**
 * O painel.
 *
 * A view nao guarda nenhum dos filtros: empresa, periodo, data e metrica sao
 * parametros de place e chegam prontos no scope. O que ela guarda e desenho.
 *
 * Os cartoes chegam numa LISTA SO e a grade os quebra sozinha. Quantas colunas a
 * grade tem e assunto de quem desenha - no painel antigo eram duas grades de quatro
 * escritas a mao, e era a apresentacao que tinha de saber disso.
 */
export function DashboardView(props: ViewProps<DashboardScope>): JSX.Element {
    return (
        <Show when={!props.scope.loading || props.scope.cards.length > 0} fallback={<Loading />}>
            <Show when={!props.scope.error} fallback={<ErrorNotice message={props.scope.error!} />}>
                <div class={Css.screen} aria-busy={props.scope.loading}>
                    <Banner scope={props.scope} />

                    {/*
                        AMPLIADO (B-43): so o grafico, na area toda do painel, com as
                        referencias no fundo. O normal fica limpo, como o cliente pediu.
                    */}
                    {/*
                        O que esta abaixo do banner e do pedido ANTERIOR enquanto a resposta
                        nao chega: fica apagado, para nao ser lido como o resultado do filtro novo.
                    */}
                    <div class={Css.content} classList={{ [Css.stale]: props.scope.refreshing }}>
                        <Show when={props.scope.expanded}>
                            <Panel
                                title="Média dos monitoramentos"
                                actions={
                                    <>
                                        <span class={Css.period}>{props.scope.period?.rangeLabel}</span>
                                        <ExpandButton scope={props.scope} />
                                    </>
                                }
                            >
                                <Show
                                    when={props.scope.metric === 'SONO'}
                                    fallback={
                                        <SeriesChart
                                            points={props.scope.series}
                                            metric={props.scope.metric}
                                            height={560}
                                            bands={props.scope.references}
                                            spread={props.scope.spread}
                                        />
                                    }
                                >
                                    {/* O SONO no formato do app Saude (B-41): barras por noite, a media e a meta. */}
                                    <DurationBars
                                        points={props.scope.series}
                                        goal={props.scope.sleepGoal}
                                        height={560}
                                    />
                                </Show>
                                <Legend scope={props.scope} />
                            </Panel>
                        </Show>

                        <Show when={!props.scope.expanded}>
                            {/*
                        Duas fileiras de quatro, como no desenho do cliente. A grade
                        quebra sozinha: a apresentacao manda UMA lista e nao sabe
                        quantas colunas ha aqui.
                    */}
                            <div class={Css.cards}>
                                <For each={props.scope.cards}>
                                    {(card) => (
                                        <MetricCard
                                            icon={card.icon}
                                            label={card.label}
                                            value={card.value}
                                            hint={card.hint}
                                        />
                                    )}
                                </For>
                            </div>

                            <div class={Css.charts}>
                                <Panel
                                    class={Css.chartWide}
                                    title="Média dos monitoramentos"
                                    actions={
                                        <>
                                            <span class={Css.period}>{props.scope.period?.rangeLabel}</span>
                                            <ExpandButton scope={props.scope} />
                                        </>
                                    }
                                >
                                    <Show
                                        when={props.scope.metric === 'SONO'}
                                        fallback={
                                            <SeriesChart
                                                points={props.scope.series}
                                                metric={props.scope.metric}
                                                height={320}
                                            />
                                        }
                                    >
                                        {/* O SONO no formato do app Saude (B-41): barras por noite, a media e a meta. */}
                                        <DurationBars
                                            points={props.scope.series}
                                            goal={props.scope.sleepGoal}
                                            height={320}
                                        />
                                    </Show>
                                </Panel>

                                <Panel title="Índice de saúde geral">
                                    <CategoryBars
                                        bars={props.scope.statusBars.map((bar) => ({
                                            category: bar.category,
                                            count: bar.count,
                                            color: bar.color,
                                            onSelect: bar.onSelect
                                        }))}
                                        height={288}
                                    />
                                </Panel>
                            </div>
                        </Show>
                    </div>
                </div>
            </Show>
        </Show>
    )
}

/**
 * A faixa escura do topo, com os filtros que valem para a tela inteira.
 *
 * Escura pelo mesmo motivo da barra lateral: separa o que CONTROLA do que e
 * mostrado, sem precisar de borda. No painel antigo era um degrade roxo-para-roxo;
 * aqui o degrade e sutil e a cor e a da marca, para o painel nao competir com os
 * proprios numeros.
 */
function Banner(props: { scope: DashboardScope }): JSX.Element {
    return (
        <section class={Css.banner}>
            <div class={Css.haloTop} />
            <div class={Css.haloBottom} />

            <div class={Css.bannerBody}>
                <div class={Css.bannerHeading}>
                    <h2 class={Css.bannerTitle}>{props.scope.bannerTitle}</h2>
                    <Show when={props.scope.refreshing}>
                        <span class={Css.refreshing} role="status">
                            <Spinner class={Css.refreshingSpinner} />
                            Atualizando…
                        </span>
                    </Show>
                </div>

                {/*
                    Os filtros numa linha PROPRIA, e nao ao lado do titulo.
                    
                    O periodo deixou de ser um combo e virou tres controles - o atalho
                    e as duas pontas -, e espremer isso ao lado de um titulo faria a
                    empresa quebrar para uma segunda linha sozinha. A EMPRESA cresce e
                    o periodo fica do tamanho que precisa: uma razao social nao cabe em
                    largura fixa, e um campo de data cabe.
                */}
                <div class={Css.bannerFilters}>
                    <Show when={props.scope.companySelector}>
                        {(selector) => <CompanyPicker scope={selector()} variant="glass" class={Css.bannerCompany} />}
                    </Show>

                    <Show when={props.scope.period}>
                        {(period) => <PeriodPicker scope={period()} onDark class={Css.bannerPeriod} />}
                    </Show>

                    {/* UM indicador para a tela inteira: o grafico e as barras andam juntos (B-37). */}
                    <Selector scope={props.scope.metricSelector} onDark />
                </div>

                <Show when={props.scope.periodDescription}>
                    {(description) => <p class={Css.bannerNote}>{description()}</p>}
                </Show>
            </div>

            <Show when={props.scope.refreshing}>
                <div class={Css.progress} aria-hidden="true" />
            </Show>
        </section>
    )
}

/** O botao de ampliar e voltar o grafico (B-43). */
function ExpandButton(props: { scope: DashboardScope }): JSX.Element {
    return (
        <button
            type="button"
            class={Css.expand}
            onClick={() => void props.scope.onToggleExpanded()}
            aria-pressed={props.scope.expanded}
            title={props.scope.expanded ? 'Voltar ao tamanho normal' : 'Ampliar o gráfico'}
            aria-label={props.scope.expanded ? 'Voltar ao tamanho normal' : 'Ampliar o gráfico'}
        >
            <Icon name={props.scope.expanded ? 'minimize' : 'maximize'} size={16} />
        </button>
    )
}

/** O que as faixas e a sombra do grafico ampliado querem dizer. */
function Legend(props: { scope: DashboardScope }): JSX.Element {
    return (
        <Show when={props.scope.references.length > 0 || props.scope.spread}>
            <div class={Css.legend}>
                <For each={props.scope.references}>
                    {(reference) => (
                        <span class={Css.legendItem}>
                            <span class={Css.legendSwatch} style={{ 'background-color': reference.color }} />
                            {reference.label}
                        </span>
                    )}
                </For>
                <Show when={props.scope.spread}>
                    <span class={Css.legendItem}>
                        <span class={`${Css.legendSwatch} ${Css.legendSpread}`} />
                        Onde está a maioria das pessoas (sem os 10% mais baixos e os 10% mais altos), em cada hora
                    </span>
                </Show>
            </div>
        </Show>
    )
}

/** Um `SelectorScope` desenhado como menu. */
function Selector(props: { scope?: SelectorScope; onDark?: boolean }): JSX.Element {
    return (
        <Show when={props.scope}>
            {(scope) => (
                <Menu
                    summary={scope().summary}
                    onDark={props.onDark}
                    options={scope().options.map((option) => ({
                        label: option.label,
                        current: option.current,
                        onSelect: option.onSelect
                    }))}
                />
            )}
        </Show>
    )
}
